import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';

class PurchaseService {
  /**
   * Purchase content
   * @param {string} contentId - Content ID to purchase
   * @param {Object} purchasePayload - Purchase payload containing amount and returnUrl
   * @returns {Promise<Object>} Purchase response
   */
  async purchaseContent(contentId, purchasePayload) {
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_API}/contents/${contentId}/purchase`,
        purchasePayload
      );
      return ResponseHandler.handleSuccess(response, 'Purchase initiated successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to purchase content');
    }
  }

  /**
   * Purchase message content (DM/chat PPV)
   * @param {string} creatorId - Creator user id (dest)
   * @param {Object} purchasePayload - Purchase payload with content_id, amount, returnUrl, etc.
   * @returns {Promise<Object>} Purchase response
   */
  async purchaseMessageContent(creatorId, purchasePayload) {
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_API}/messages/${creatorId}/purchase_content`,
        purchasePayload
      );
      return ResponseHandler.handleSuccess(response, 'Purchase initiated successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to purchase message content');
    }
  }

  /**
   * Cancel/delete a purchase
   * @param {string} purchaseId - Purchase ID to cancel
   * @param {boolean} isManualRemove - Whether to manually remove the purchase (true) or cancel the purchase (false)  
   * @returns {Promise<Object>} Cancel purchase response
   */
  async cancelPurchase(purchaseId, isManualRemove) {
    try {
      const response = await apiClient.delete(
        `${process.env.REACT_APP_API}/contents/${purchaseId}/purchase/${isManualRemove}`
      );
      return ResponseHandler.handleSuccess(response, 'Purchase cancelled successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to cancel purchase');
    }
  }

  /**
   * Check if content has been purchased by the current user
   * @param {string} contentId - Content ID to check
   * @returns {Promise<Object>} Purchase check response
   */
  async checkContentPurchase(contentId) {
    try {
      const response = await apiClient.get(
        `${process.env.REACT_APP_API}/contents/${contentId}/purchase-check`
      );
      return ResponseHandler.handleSuccess(response, 'Purchase check completed successfully');
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;

      if (status === 404) {
        return {
          success: true,
          status: 404,
          data: data || { purchased: false },
          message: data?.message || 'Content not purchased',
        };
      }

      return ResponseHandler.handleError(error, 'Failed to check content purchase');
    }
  }

  /**
   * Cancel/delete a message content purchase (DM/chat PPV)
   * @param {string} purchaseId - Message purchase content ID to cancel
   * @param {boolean} isManualRemove - Whether to manually remove the purchase (true) or cancel the purchase (false)
   * @returns {Promise<Object>} Cancel purchase response
   */
  async cancelMessagePurchase(purchaseId, isManualRemove) {
    try {
      const response = await apiClient.delete(
        `${process.env.REACT_APP_API}/messages/purchase_content/${purchaseId}/${isManualRemove}`
      );
      return ResponseHandler.handleSuccess(response, 'Purchase cancelled successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to cancel purchase');
    }
  }
}

export default new PurchaseService();

